import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';

import React from 'react';
import { Icon, type Icons } from '../icon';
import { cn } from '../lib/utils';
import { LoadingSpinner } from './spinner';

const buttonVariants = cva(
  "cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-2xl text-body-14 transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-3 shadow-xs hover:bg-primary/90',
        primary:
          'rounded-md border border-primary-70 bg-gradient-to-r from-[var(--color-gradient-green-end)] from-10% to-[var(--color-gradient-green-start)] to-90% text-body-13 text-white shadow-sm transition-opacity hover:opacity-90',
        destructive:
          'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40',
        outline: 'border-1 border-primary-10 bg-white text-primary hover:bg-primary-5',
        secondary:
          'bg-primary-alpha-5 text-primary-alpha-60 shadow-xs hover:bg-primary-alpha-10 border-0',
        ghost: 'hover:bg-accent hover:text-accent-primary-3 hover:bg-primary-5',
        link: 'text-primary underline-offset-4 hover:underline',
        'secondary-60': 'bg-secondary-60 font-semibold',
        danger:
          'bg-status-danger text-white shadow-xs hover:bg-status-danger/90 focus-visible:ring-status-danger/20 dark:focus-visible:ring-status-danger/40',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-7 rounded-sm gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-sm px-6 has-[>svg]:px-4',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  icon?: Icons;
}

const Button = ({
  className,
  variant,
  icon,
  size,
  asChild = false,
  isLoading,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot : 'button';
  const [isInterallyLoading, setIsInterallyLoading] = React.useState(false);

  const onClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!props.onClick) {
        return undefined;
      }

      setIsInterallyLoading(true);
      Promise.resolve(props.onClick(event)).finally(() => setIsInterallyLoading(false));
    },
    [props.onClick],
  );

  // When asChild is true, Slot expects exactly one child element
  // so we wrap the content in a span to ensure a single child
  const content = (
    <>
      {icon && <Icon name={icon} className="text-primary shrink-0 text-body-14" />}
      {isInterallyLoading || isLoading ? <LoadingSpinner /> : props.children}
    </>
  );

  return (
    <Comp
      className={cn('w-auto', buttonVariants({ variant, size, className }))}
      {...props}
      disabled={props.disabled || isLoading || isInterallyLoading}
      onClick={onClick}
    >
      {asChild ? <span className="contents">{content}</span> : content}
    </Comp>
  );
};
Button.displayName = 'Button';

export { Button, buttonVariants };
